// 选片功能前端 API（独立于 delivery）：公开读取选片链接详情、提交选片结果。
// 复用通用的 R2 文件夹列举与缩略图能力（getDeliveryApiBase / listDeliveryFolder），
// 这些是与功能无关的基础设施；选片业务逻辑全部独立。
import { getDeliveryApiBase } from "./deliveryApi";
import { getTranslator } from "../i18n";

export function getSelectionApiBase() {
    return getDeliveryApiBase();
}

export function isSelectionApiConfigured() {
    return Boolean(getSelectionApiBase());
}

// 凭短 ID 从 Worker（D1）公开读取选片链接详情。
// 命中返回详情；404 返回 null；410 抛 EXPIRED；网络/服务异常抛错（含 code/status）。
export async function fetchSelectionInfo(id) {
    const apiBase = getSelectionApiBase();
    if (!apiBase) {
        const error = new Error(getTranslator()("selection.api.missingApi"));
        error.code = "MISSING_API";
        throw error;
    }

    const url = `${apiBase}/api/selection/info/${encodeURIComponent(id)}?_=${Date.now()}`;

    let response;
    try {
        response = await fetch(url, { cache: "no-store" });
    } catch {
        const error = new Error("SELECTION_LOAD_FAILED");
        error.code = "NETWORK_ERROR";
        throw error;
    }

    if (response.status === 404) {
        return null;
    }

    if (response.status === 410) {
        const body = await response.json().catch(() => ({}));
        const error = new Error("SELECTION_EXPIRED");
        error.code = "EXPIRED";
        error.status = 410;
        error.archiveMarkdown = body.error?.archiveMarkdown || body.archiveMarkdown || "";
        throw error;
    }

    if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        const error = new Error(body.error?.message || "SELECTION_LOAD_FAILED");
        error.code = "API_ERROR";
        error.status = response.status;
        throw error;
    }

    const body = await response.json().catch(() => ({}));
    return body.selection || null;
}

// 公开读取：按 token + 选片人姓名取上次提交的已选照片（用于同名自动回填）。
// 返回 [{ path, name }]；任何异常都安全降级为空数组（不阻塞选片流程）。
export async function fetchPreviousPicks({ token, selectorName }) {
    const apiBase = getSelectionApiBase();
    if (!apiBase || !token || !selectorName) {
        return [];
    }
    const url = `${apiBase}/api/selection/picks?token=${encodeURIComponent(
        token
    )}&name=${encodeURIComponent(selectorName)}&_=${Date.now()}`;

    let response;
    try {
        response = await fetch(url, { cache: "no-store" });
    } catch {
        return [];
    }
    if (!response.ok) {
        return [];
    }
    const body = await response.json().catch(() => ({}));
    return Array.isArray(body.picks) ? body.picks : [];
}

// 公开提交选片结果：{ token, selectorName, picks:[{path,name}] }。
// 成功返回 { id, createdAt, photoCount }；过期抛 EXPIRED；其余抛错（含 status）。
export async function submitSelection({ token, selectorName, picks }) {
    const apiBase = getSelectionApiBase();
    if (!apiBase) {
        const error = new Error(getTranslator()("selection.api.missingApi"));
        error.code = "MISSING_API";
        throw error;
    }

    let response;
    try {
        response = await fetch(`${apiBase}/api/selection/submit`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            cache: "no-store",
            body: JSON.stringify({ token, selectorName, picks }),
        });
    } catch {
        const error = new Error("SELECTION_SUBMIT_FAILED");
        error.code = "NETWORK_ERROR";
        throw error;
    }

    if (response.status === 410) {
        const error = new Error("SELECTION_EXPIRED");
        error.code = "EXPIRED";
        error.status = 410;
        throw error;
    }

    if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        const error = new Error(body.error?.message || "SELECTION_SUBMIT_FAILED");
        error.code = "API_ERROR";
        error.status = response.status;
        throw error;
    }

    const body = await response.json().catch(() => ({}));
    return body.submission || null;
}
